import { useEffect, useMemo, useState } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Col,
  message,
  Row,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { postReq, getReq } from "../api.js";
import dayjs from "dayjs";
import LeaveMetricCard from "../components/LeaveMetricCard.jsx";
import ApplyLeaveModal from "../components/ApplyLeaveModal.jsx";

const { Title } = Typography;

const MyLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 1,
    search: "",
  });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await postReq("/user/getLeaves", {
        page: paginationModel.page,
        limit: paginationModel.pageSize,
        search: "",
      });
      if (res.status === 200) {
        setLeaves(res.data.data);
        setTotal(res.data.total);
      } else {
        message.error(res.message || "Error while fetching data");
      }
    } catch (error) {
      message.error(error.message || "Error while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const res = await getReq("/user/leaveBalance");
      if (res.status === 200) {
        setLeaveBalance(res.data);
      } else {
        message.error(res.message || "Error while fetching data");
      }
    } catch (error) {
      message.error(error.message || "Error while fetching leave balance");
    }
  };

  useEffect(() => {
    fetchLeaveBalance();
  }, []);

  useEffect(() => {
    fetchLeaves();
  }, [paginationModel]);

  const statusConfig = {
    APPROVED: { color: "success" },
    REJECTED: { color: "error" },
    PENDING: { color: "warning" },
  };

  const columns = useMemo(
    () => [
      {
        title: "Action",
        key: "action",
        width: 80,
        render: (_, row) =>
          row.status === "PENDING" ? (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setEdit(row);
                  setOpen(true);
                }}
              />
            </Tooltip>
          ) : null,
      },
      {
        title: "Leave Type",
        dataIndex: "leaveType",
        key: "leaveType",
        width: 140,
      },
      {
        title: "From Date",
        dataIndex: "fromDate",
        key: "fromDate",
        width: 130,
        render: (val) => dayjs(val).format("DD/MM/YYYY"),
      },
      {
        title: "To Date",
        dataIndex: "toDate",
        key: "toDate",
        width: 130,
        render: (val) => dayjs(val).format("DD/MM/YYYY"),
      },
      {
        title: "Days",
        dataIndex: "quantity",
        key: "quantity",
        width: 80,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status) => {
          const cfg = statusConfig[status] || { color: "default" };
          return (
            <Tag color={cfg.color} style={{ fontWeight: 500 }}>
              {status}
            </Tag>
          );
        },
      },
      {
        title: "Updated On",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 170,
        render: (val) => dayjs(val).format("DD/MM/YYYY HH:mm"),
      },
      {
        title: "Approved By",
        dataIndex: "approvedBy",
        key: "approvedBy",
        render: (approvedBy) =>
          approvedBy ? `${approvedBy.firstName} ${approvedBy.lastName}` : "-",
      },
    ],
    [],
  );

  const metrics = [
    {
      title: "Paid Leave",
      value: leaveBalance?.plAvailable ?? 0,
      subtitle: "Available",
      icon: <CalendarOutlined />,
      bgColor: "#E6F7F5",
    },
    {
      title: "Sick Leave",
      value: leaveBalance?.slAvailable ?? 0,
      subtitle: "Available",
      icon: <CalendarOutlined />,
      bgColor: "#E8F1FD",
    },
    {
      title: "Approved PL",
      value: leaveBalance?.plUsed ?? 0,
      subtitle: "Total",
      icon: <CheckCircleOutlined />,
      bgColor: "#E9F9EE",
    },
    {
      title: "Approved SL",
      value: leaveBalance?.slUsed ?? 0,
      subtitle: "Total",
      icon: <CheckCircleOutlined />,
      bgColor: "#ECFDF5",
    },
    {
      title: "Rejected",
      value: leaveBalance?.rejectedLeave ?? 0,
      subtitle: "Total",
      icon: <CloseCircleOutlined />,
      bgColor: "#FEECEC",
    },
  ];

  return (
    <div style={{ padding: 4 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          My Leaves
        </Title>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => setOpen(true)}
        >
          Apply Leave
        </Button>
      </div>

      {/* Metric Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {metrics.map((m) => (
          <Col key={m.title} xs={24} sm={12} md={8} lg={4} xl={4}>
            <LeaveMetricCard
              title={m.title}
              value={m.value}
              subtitle={m.subtitle}
              icon={m.icon}
              bgColor={m.bgColor}
            />
          </Col>
        ))}
      </Row>

      {/* Data Table */}
      <Table
        loading={loading}
        dataSource={leaves}
        columns={columns}
        rowKey={(row) => row._id}
        scroll={{ x: "max-content" }}
        pagination={{
          current: paginationModel.page,
          pageSize: paginationModel.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20"],
          onChange: (page, pageSize) => {
            setPaginationModel({ page, pageSize, search: "" });
          },
          showTotal: (tot) => `Total ${tot} records`,
        }}
      />

      {/* Apply / Edit Leave Modal */}
      <ApplyLeaveModal
        open={open}
        edit={edit}
        handleClose={() => {
          setEdit(null);
          setOpen(false);
          fetchLeaveBalance();
          fetchLeaves();
        }}
      />
    </div>
  );
};

export default MyLeaves;
